import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react'
import { notificationsApi, type Notification as ApiNotification } from 'lib/api'
import { SSEClient } from 'lib/sseClient'
import { pushNotificationManager } from 'lib/pushNotifications'
import { useAuth } from 'contexts/AuthContext'

// Frontend notification interface (compatible with backend)
export interface Notification {
  id: number | string // Backend uses number, legacy localStorage uses string
  type: 'system' | 'alert' | 'update' | 'reminder' | 'error' | 'success' | 'info'
  title: string
  message: string
  // Support both snake_case (backend) and camelCase (legacy)
  title_key?: string
  titleKey?: string
  message_key?: string
  messageKey?: string
  message_params?: Record<string, any>
  messageParams?: Record<string, any>
  timestamp?: Date
  read: boolean
  priority: 'low' | 'medium' | 'high' | 'urgent'
  category?: string
  duration?: number
  action?: {
    label: string
    actionType?: string
    onClick?: () => void
  }
  batchId?: string
  // Backend fields
  read_at?: string
  created_at?: string
  expires_at?: string
  action_url?: string
  action_type?: string
  action_label?: string
  metadata?: Record<string, any>
  related_object_type?: string
  related_object_id?: number
}

interface NotificationState {
  notifications: Notification[]
  loading: boolean
  error: string | null
  lastFetch: number | null
}

type NotificationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOAD_NOTIFICATIONS'; payload: Notification[] }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: number | string }
  | { type: 'MARK_AS_READ'; payload: number | string }
  | { type: 'MARK_AS_UNREAD'; payload: number | string }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'CLEAR_ALL' }

const initialState: NotificationState = {
  notifications: [],
  loading: false,
  error: null,
  lastFetch: null
}

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'LOAD_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        loading: false,
        error: null,
        lastFetch: Date.now()
      }
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications.filter(n => n.id !== action.payload.id)]
      }
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      }
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: true, read_at: new Date().toISOString() } : n
        )
      }
    case 'MARK_AS_UNREAD':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: false, read_at: undefined } : n
        )
      }
    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => ({
          ...n,
          read: true,
          read_at: new Date().toISOString()
        }))
      }
    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: []
      }
    default:
      return state
  }
}

// Convert API notification to frontend format
export const convertApiNotification = (apiNotif: ApiNotification): Notification => {
  return {
    id: apiNotif.id,
    type: apiNotif.type,
    title: apiNotif.title,
    message: apiNotif.message,
    // Support both formats for compatibility
    title_key: apiNotif.title_key,
    titleKey: apiNotif.title_key, // Also set camelCase for backward compatibility
    message_key: apiNotif.message_key,
    messageKey: apiNotif.message_key, // Also set camelCase for backward compatibility
    message_params: apiNotif.message_params,
    messageParams: apiNotif.message_params, // Also set camelCase for backward compatibility
    timestamp: new Date(apiNotif.created_at),
    read: apiNotif.read,
    priority: apiNotif.priority,
    category: apiNotif.category,
    batchId: apiNotif.metadata?.batchId || apiNotif.metadata?.batch_id,
    read_at: apiNotif.read_at,
    created_at: apiNotif.created_at,
    expires_at: apiNotif.expires_at,
    action_url: apiNotif.action_url,
    action_type: apiNotif.action_type,
    action_label: apiNotif.action_label,
    metadata: apiNotif.metadata,
    related_object_type: apiNotif.related_object_type,
    related_object_id: apiNotif.related_object_id,
    action: apiNotif.action_url ? {
      label: apiNotif.action_label || 'View',
      actionType: apiNotif.action_type
    } : undefined
  }
}

// Type for creating notifications (allows both camelCase and snake_case)
export type CreateNotificationInput = {
  type: 'system' | 'alert' | 'update' | 'reminder' | 'error' | 'success' | 'info'
  title: string
  message: string
  // Support both formats
  titleKey?: string
  title_key?: string
  messageKey?: string
  message_key?: string
  messageParams?: Record<string, any>
  message_params?: Record<string, any>
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  category?: string
  duration?: number
  action?: {
    label: string
    actionType?: string
    onClick?: () => void
  }
  // Backend fields (snake_case)
  action_url?: string
  action_type?: string
  action_label?: string
  batchId?: string
  metadata?: Record<string, any>
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null
  addNotification: (notification: CreateNotificationInput) => Promise<void>
  removeNotification: (id: number | string) => Promise<void>
  markAsRead: (id: number | string) => Promise<void>
  markAsUnread: (id: number | string) => Promise<void>
  markAllAsRead: () => Promise<void>
  clearAll: () => Promise<void>
  refreshNotifications: () => Promise<void>
  clearOldNotifications: (daysOld?: number) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

const POLL_INTERVAL = 30000 // Poll every 30 seconds (fallback if SSE fails)
const FETCH_COOLDOWN = 5000 // Don't fetch more than once per 5 seconds

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(notificationReducer, initialState)
  const sseClientRef = useRef<SSEClient | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const { isAuthenticated, loading: authLoading } = useAuth()

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const response = await notificationsApi.list({ page_size: 100 })
      const apiNotifications = response.data.results || []
      const notifications = apiNotifications.map(convertApiNotification)
      dispatch({ type: 'LOAD_NOTIFICATIONS', payload: notifications })
    } catch (error: any) {
      console.error('Failed to fetch notifications:', error)
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.message || 'Failed to load notifications' })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  // Initialize SSE connection for real-time notifications
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      sseClientRef.current?.disconnect()
      sseClientRef.current = null
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      return
    }

    const sseClient = new SSEClient(
      // onNotification
      (notification) => {
        dispatch({ type: 'ADD_NOTIFICATION', payload: notification })
        
        // Show browser push notification
        if (notification.priority !== 'low' && !notification.read) {
          pushNotificationManager.showNotification(notification.title, {
            body: notification.message,
            tag: notification.category || 'general',
            data: { notificationId: notification.id },
            requireInteraction: notification.priority === 'urgent',
          })
        }
        
      },
      // onUnreadCountChange
      (count) => {
        // Update unread count (we'll calculate it from notifications)
      },
      // onError
      (error) => {
        console.error('SSE error:', error)
        // Fall back to polling if SSE fails
        if (!pollIntervalRef.current) {
          pollIntervalRef.current = setInterval(() => {
            if (!state.lastFetch || Date.now() - state.lastFetch > FETCH_COOLDOWN) {
              fetchNotifications()
            }
          }, POLL_INTERVAL)
        }
      }
    )

    sseClientRef.current = sseClient
    
    // Try to connect
    try {
      sseClient.connect()
    } catch (error) {
      console.error('Failed to connect SSE:', error)
      // Fall back to polling
      pollIntervalRef.current = setInterval(() => {
        if (!state.lastFetch || Date.now() - state.lastFetch > FETCH_COOLDOWN) {
          fetchNotifications()
        }
      }, POLL_INTERVAL)
    }

    // Cleanup
    return () => {
      sseClient.disconnect()
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [isAuthenticated, authLoading, fetchNotifications])

  // Load notifications once authenticated (fallback if SSE is not available)
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return
    }
    if (!sseClientRef.current?.isConnected()) {
      fetchNotifications()
    }
  }, [fetchNotifications, isAuthenticated, authLoading])

  // Poll for new notifications (fallback if SSE fails)
  useEffect(() => {
    if (authLoading || !isAuthenticated || sseClientRef.current?.isConnected()) {
      return
    }

    const interval = setInterval(() => {
      if (!state.lastFetch || Date.now() - state.lastFetch > FETCH_COOLDOWN) {
        fetchNotifications()
      }
    }, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [fetchNotifications, state.lastFetch, isAuthenticated, authLoading])

  const addNotification = async (notification: CreateNotificationInput) => {
    try {
      // Convert to API format - handle both snake_case (backend) and camelCase (legacy)
      const apiData: any = {
        type: notification.type,
        title: notification.title,
        message: notification.message,
        title_key: notification.title_key || notification.titleKey,
        message_key: notification.message_key || notification.messageKey,
        message_params: notification.message_params || notification.messageParams || {},
        category: notification.category,
        priority: notification.priority || 'medium',
        action_url: notification.action_url || (notification.action?.actionType ? undefined : undefined),
        action_type: notification.action_type || notification.action?.actionType,
        action_label: notification.action_label || notification.action?.label,
        metadata: notification.batchId ? { batchId: notification.batchId } : (notification.metadata || {})
      }

      const response = await notificationsApi.create(apiData)
      const newNotification = convertApiNotification(response.data)
      dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification })
      
      // Show browser push notification
      if (newNotification.priority !== 'low' && !newNotification.read) {
        pushNotificationManager.showNotification(newNotification.title, {
          body: newNotification.message,
          tag: newNotification.category || 'general',
          data: { notificationId: newNotification.id },
          requireInteraction: newNotification.priority === 'urgent',
        })
      }
      
    } catch (error: any) {
      console.error('Failed to create notification:', error)
      // Fallback: add locally if API fails (for backward compatibility)
      const fallbackNotification: Notification = {
        ...notification,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
        read: false,
        priority: notification.priority || 'medium'
      }
      dispatch({ type: 'ADD_NOTIFICATION', payload: fallbackNotification })
    }
  }

  const removeNotification = async (id: number | string) => {
    try {
      if (typeof id === 'number') {
        await notificationsApi.delete(id)
      }
      dispatch({ type: 'REMOVE_NOTIFICATION', payload: id })
    } catch (error: any) {
      console.error('Failed to delete notification:', error)
      // Still remove from local state
      dispatch({ type: 'REMOVE_NOTIFICATION', payload: id })
    }
  }

  const markAsRead = async (id: number | string) => {
    try {
      if (typeof id === 'number') {
        await notificationsApi.markAsRead(id)
      }
      dispatch({ type: 'MARK_AS_READ', payload: id })
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error)
      // Still update local state
      dispatch({ type: 'MARK_AS_READ', payload: id })
    }
  }

  const markAsUnread = async (id: number | string) => {
    try {
      if (typeof id === 'number') {
        await notificationsApi.markAsUnread(id)
      }
      dispatch({ type: 'MARK_AS_UNREAD', payload: id })
    } catch (error: any) {
      console.error('Failed to mark notification as unread:', error)
      // Still update local state
      dispatch({ type: 'MARK_AS_UNREAD', payload: id })
    }
  }

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead()
      dispatch({ type: 'MARK_ALL_AS_READ' })
    } catch (error: any) {
      console.error('Failed to mark all as read:', error)
      // Still update local state
      dispatch({ type: 'MARK_ALL_AS_READ' })
    }
  }

  const clearAll = async () => {
    try {
      await notificationsApi.clearAll()
      dispatch({ type: 'CLEAR_ALL' })
    } catch (error: any) {
      console.error('Failed to clear notifications:', error)
      // Still clear local state
      dispatch({ type: 'CLEAR_ALL' })
    }
  }

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications()
  }, [fetchNotifications])

  const clearOldNotifications = (daysOld: number = 30) => {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysOld)
    
    const filteredNotifications = state.notifications.filter(n => {
      const notifDate = n.timestamp || (n.created_at ? new Date(n.created_at) : null)
      return notifDate && notifDate >= cutoffDate
    })
    
    if (filteredNotifications.length !== state.notifications.length) {
      dispatch({ type: 'LOAD_NOTIFICATIONS', payload: filteredNotifications })
    }
  }

  // Load notification preferences for push and sound managers
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await notificationsApi.getPreferences()
        pushNotificationManager.setPreferences(response.data)
        
        // Don't automatically request permission - let user enable it manually in settings
        // Permission will be requested when user explicitly enables browser notifications
      } catch (error) {
        console.error('Failed to load notification preferences:', error)
      }
    }
    
    loadPreferences()
  }, [])

  const unreadCount = state.notifications.filter(n => !n.read).length

  return (
    <NotificationContext.Provider
      value={{
        notifications: state.notifications,
        unreadCount,
        loading: state.loading,
        error: state.error,
        addNotification,
        removeNotification,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        clearAll,
        refreshNotifications,
        clearOldNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
